import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { upperFirst } from "lodash";
import { Dispatch, PropsWithChildren, PropsWithRef, ReactElement, useState } from "react";
import { ErrorBoundary, ErrorBoundaryProps, FallbackProps } from "react-error-boundary";
import { toast } from "../Toast/toast";

const DefaultErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  return (
    <Alert
      severity="error"
      action={
        <Button color="inherit" onClick={resetErrorBoundary} sx={{ whiteSpace: "nowrap" }}>
          Try again
        </Button>
      }
    >
      <AlertTitle>Something went wrong...</AlertTitle>
      {}
      <Typography>{upperFirst(error.message)}</Typography>
    </Alert>
  );
};

export { useErrorBoundary } from "react-error-boundary";

/**
 * @deprecated
 * If you have access to the error during render, just throw it there yourself:
 * ```tsx
 * function Component() {
 *  const { data, loading, error } = useSomeQuery();
 *  if (error) throw error;
 *
 *  // ...render component
 * }
 * ```
 *
 * For callbacks, consider `useErrorBoundary` instead:
 * ```tsx
 * function Component() {
 *  const { showBoundary } = useErrorBoundary();
 *
 *  const onClick = async () => {
 *    try {
 *      await someAsyncFunction();
 *     } catch (e) {
 *      showBoundary(e);
 *     }
 *  }
 *
 *  // ...render component
 * }
 * ```
 */
export function useErrorHandler(): Dispatch<unknown> {
  const [error, setError] = useState<unknown | null>(null);

  if (error) throw error as Error;

  return setError;
}

export type { FallbackProps } from "react-error-boundary";
export type ErrorHandlerProps = {
  /**
   * Determines if the ErrorHandler should display a toast when the `onError` callback is invoked
   * @default true
   */
  showToast?: boolean;
} & Partial<ErrorBoundaryProps>;

/**
 * A simple wrapper around the [`react-error-boundary`](https://github.com/bvaughn/react-error-boundary) library
 *
 * Provides a default `FallbackComponent` if none is supplied and can automatically display a toast when the onError
 * callback is invoked
 *
 * @example Apollo Query
 * const Parent = () => (
 *   <ErrorHandler>
 *     <QueryComponent />
 *   </ErrorHandler>
 * );
 *
 * const QueryComponent = () => {
 *   const { data, error } = useSomeQuery();
 *
 *   if (error) throw error;
 *
 *   return (
 *     <div>{data?.attribute}</div>
 *   );
 * };
 *
 * @example Apollo Mutation
 * const Parent = () => (
 *   <ErrorHandler>
 *     <MutationComponent1 />
 *     <MutationComponent2 />
 *   </ErrorHandler>
 * );
 *
 * const MutationComponent1 = () => {
 *   const [mutation, { error }] = useSomeMutation();
 *
 *   if (error) throw error;
 *
 *   return (
 *     <button onClick={() => mutation()}>click</button>
 *   );
 * };
 *
 * const MutationComponent2 = () => {
 *   const [mutation] = useSomeMutation();
 *  const { showBoundary: handleError } = useErrorBoundary();
 *
 *   const onClick = async () => {
 *     try {
 *       await mutation();
 *     } catch (e) {
 *       handleError(e);
 *     }
 *   }
 *
 *   return (
 *     <button onClick={() => mutation()}>click</button>
 *   );
 * };
 *
 */
export const ErrorHandler = ({
  onError,
  children,
  showToast = true,
  ...props
}: PropsWithRef<PropsWithChildren<ErrorHandlerProps>>): ReactElement => {
  const { fallbackRender, fallback } = props;
  const hasAlternativeRender = !!fallbackRender || !!fallback;

  const updateError: ErrorBoundaryProps["onError"] = (e, ...rest) => {
    if (showToast) toast.error(upperFirst(e.message));
    onError?.(e, ...rest);
  };

  return (
    // @ts-ignore - TypeScript getting confused about the different render methods
    <ErrorBoundary
      FallbackComponent={!hasAlternativeRender ? DefaultErrorFallback : undefined}
      {...props}
      onError={updateError}
    >
      {children}
    </ErrorBoundary>
  );
};
